import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { tr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Users,
  Plus,
  X,
  Award,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { employeesAPI, employeeBonusesAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import PageToolbar from '../components/PageToolbar';

const Bonuses = () => {
  const { userRole } = useAuth();
  const { showSuccess, showError } = useNotification();

  // State
  const [employees, setEmployees] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [monthlyBonuses, setMonthlyBonuses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingBonus, setEditingBonus] = useState(null);

  // Loading states for buttons
  const [isSubmittingEmployee, setIsSubmittingEmployee] = useState(false);
  const [isDeletingEmployee, setIsDeletingEmployee] = useState(null);
  const [isSubmittingBonus, setIsSubmittingBonus] = useState(false);
  const [isDeletingBonus, setIsDeletingBonus] = useState(null);

  // Date filter - TransactionTracking ile aynı yapı
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });

  // Period type filter (daily, monthly, yearly)
  const [periodType, setPeriodType] = useState('daily');

  // Navigation functions for date range
  const handlePreviousPeriod = () => {
    const diffTime = endDate.getTime() - startDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const newStartDate = new Date(startDate);
    newStartDate.setDate(newStartDate.getDate() - diffDays);
    const newEndDate = new Date(endDate);
    newEndDate.setDate(newEndDate.getDate() - diffDays);

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleNextPeriod = () => {
    const diffTime = endDate.getTime() - startDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const newStartDate = new Date(startDate);
    newStartDate.setDate(newStartDate.getDate() + diffDays);
    const newEndDate = new Date(endDate);
    newEndDate.setDate(newEndDate.getDate() + diffDays);

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Period type handlers
  const handlePeriodTypeChange = (type) => {
    setPeriodType(type);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (type === 'daily') {
      const start = new Date(today);
      const end = new Date(today);
      end.setHours(23, 59, 59, 999);
      setStartDate(start);
      setEndDate(end);
    } else if (type === 'monthly') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      setStartDate(start);
      setEndDate(end);
    } else if (type === 'yearly') {
      const start = new Date(today.getFullYear(), 0, 1);
      const end = new Date(today.getFullYear(), 11, 31);
      end.setHours(23, 59, 59, 999);
      setStartDate(start);
      setEndDate(end);
    }
  };

  // Form data
  const [employeeFormData, setEmployeeFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    hire_date: new Date().toISOString().split('T')[0]
  });

  const [bonusFormData, setBonusFormData] = useState({
    employee_id: '',
    amount: '',
    bonus_date: ''
  });

  // Format date for display
  const formatDateForDisplay = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString('tr-TR', options);
  };

  const formatDateForAPI = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    const year = normalized.getFullYear();
    const month = (normalized.getMonth() + 1).toString().padStart(2, '0');
    const day = normalized.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };



  const totalBonuses = useMemo(() => (
    bonuses.reduce((sum, bonus) => sum + parseFloat(bonus.amount || 0), 0)
  ), [bonuses]);

  const bonusesByEmployee = useMemo(() => {
    return bonuses.reduce((acc, bonus) => {
      if (!bonus.employee_id) return acc;
      acc[bonus.employee_id] = (acc[bonus.employee_id] || 0) + parseFloat(bonus.amount || 0);
      return acc;
    }, {});
  }, [bonuses]);

  // Calculate monthly bonuses for each employee (for current month only, regardless of date filter)
  const monthlyBonusesByEmployee = useMemo(() => {
    return monthlyBonuses.reduce((acc, bonus) => {
      if (!bonus.employee_id) return acc;
      acc[bonus.employee_id] = (acc[bonus.employee_id] || 0) + parseFloat(bonus.amount || 0);
      return acc;
    }, {});
  }, [monthlyBonuses]);

  // Fetch data
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await employeesAPI.getEmployees();
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      showError('Çalışanlar yüklenirken hata oluştu');
    }
  }, [showError]);

  const fetchBonuses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await employeeBonusesAPI.getBonuses({
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate)
      });

      const bonusData = response.data.bonuses || [];
      setBonuses(bonusData);
    } catch (error) {
      console.error('Error fetching bonuses:', error);
      showError('Primler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, showError]);

  // Fetch monthly bonuses for current month (for display under employee names)
  const fetchMonthlyBonuses = useCallback(async () => {
    try {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      endOfMonth.setHours(23, 59, 59, 999);

      const response = await employeeBonusesAPI.getBonuses({
        startDate: formatDateForAPI(startOfMonth),
        endDate: formatDateForAPI(endOfMonth)
      });

      const bonusData = response.data.bonuses || [];
      setMonthlyBonuses(bonusData);
    } catch (error) {
      console.error('Error fetching monthly bonuses:', error);
      showError('Aylık primler yüklenirken hata oluştu');
    }
  }, [showError]);

  // Initial load
  useEffect(() => {
    if (userRole !== 'admin') return;
    fetchEmployees();
    fetchMonthlyBonuses();
  }, [userRole, fetchEmployees, fetchMonthlyBonuses]);

  useEffect(() => {
    if (userRole !== 'admin') return;
    fetchBonuses();
  }, [userRole, fetchBonuses]);

  // Employee form handlers
  const handleEmployeeFormChange = (e) => {
    const { name, value } = e.target;
    setEmployeeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmployeeSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingEmployee) return;

    try {
      setIsSubmittingEmployee(true);
      if (editingEmployee) {
        await employeesAPI.updateEmployee(editingEmployee.id, employeeFormData);
        showSuccess('Çalışan başarıyla güncellendi');
      } else {
        await employeesAPI.createEmployee(employeeFormData);
        showSuccess('Çalışan başarıyla eklendi');
      }

      setShowEmployeeModal(false);
      resetEmployeeForm();
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      showError(editingEmployee ? 'Çalışan güncellenirken hata oluştu' : 'Çalışan eklenirken hata oluştu');
    } finally {
      setIsSubmittingEmployee(false);
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeFormData({
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      hire_date: new Date().toISOString().split('T')[0]
    });
    setEditingEmployee(null);
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeFormData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      phone: employee.phone || '',
      email: employee.email || '',
      hire_date: employee.hire_date || new Date().toISOString().split('T')[0]
    });
    setShowEmployeeModal(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Bu çalışanı silmek istediğinizden emin misiniz?')) return;
    if (isDeletingEmployee === employeeId) return;

    try {
      setIsDeletingEmployee(employeeId);
      await employeesAPI.deleteEmployee(employeeId);
      showSuccess('Çalışan başarıyla silindi');
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      showError('Çalışan silinirken hata oluştu');
    } finally {
      setIsDeletingEmployee(null);
    }
  };

  // Bonus form handlers
  const handleBonusFormChange = (e) => {
    const { name, value } = e.target;
    setBonusFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBonusSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingBonus) return;

    if (!bonusFormData.employee_id) {
      showError('Lütfen bir çalışan seçin');
      return;
    }

    if (!bonusFormData.amount || parseFloat(bonusFormData.amount) <= 0) {
      showError('Lütfen geçerli bir tutar girin');
      return;
    }

    try {
      setIsSubmittingBonus(true);
      const payload = {
        employee_id: bonusFormData.employee_id,
        amount: bonusFormData.amount,
        bonus_date: bonusFormData.bonus_date
      };

      if (editingBonus) {
        await employeeBonusesAPI.updateBonus(editingBonus.id, payload);
        showSuccess('Prim başarıyla güncellendi');
      } else {
        await employeeBonusesAPI.createBonus(payload);
        showSuccess('Prim başarıyla eklendi');
      }

      setShowBonusModal(false);
      resetBonusForm();
      fetchBonuses();
      fetchMonthlyBonuses();
    } catch (error) {
      console.error('Error saving bonus:', error);
      showError(editingBonus ? 'Prim güncellenirken hata oluştu' : 'Prim eklenirken hata oluştu');
    } finally {
      setIsSubmittingBonus(false);
    }
  };

  const resetBonusForm = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    setBonusFormData({
      employee_id: '',
      amount: '',
      bonus_date: `${year}-${month}-${day}`
    });
    setEditingBonus(null);
  };

  const handleEditBonus = (bonus) => {
    setEditingBonus(bonus);
    setBonusFormData({
      employee_id: bonus.employee_id,
      amount: bonus.amount,
      bonus_date: bonus.bonus_date
    });
    setShowBonusModal(true);
  };

  const handleDeleteBonus = async (bonusId) => {
    if (!window.confirm('Bu primi silmek istediğinizden emin misiniz?')) return;
    if (isDeletingBonus === bonusId) return;

    try {
      setIsDeletingBonus(bonusId);
      await employeeBonusesAPI.deleteBonus(bonusId);
      showSuccess('Prim başarıyla silindi');
      fetchBonuses();
      fetchMonthlyBonuses();
    } catch (error) {
      console.error('Error deleting bonus:', error);
      showError('Prim silinirken hata oluştu');
    } finally {
      setIsDeletingBonus(null);
    }
  };

  // Access control
  if (userRole !== 'admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-700 mb-2">Yetkisiz Erişim</h2>
          <p className="text-gray-500">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <PageToolbar
        subtitle={`Seçili dönem: ${startDate.toLocaleDateString('tr-TR')} - ${endDate.toLocaleDateString('tr-TR')}`}
        right={
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-2 text-sm text-indigo-700">
                Toplam prim: <span className="font-semibold">{totalBonuses.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</span>
              </div>
              <button
                onClick={() => {
                  resetEmployeeForm();
                  setShowEmployeeModal(true);
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700"
              >
                <Plus className="h-4 w-4" />
                Çalışan Ekle
              </button>
              <button
                onClick={() => {
                  resetBonusForm();
                  setShowBonusModal(true);
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4" />
                Prim Ekle
              </button>
            </div>
          </>
        }
      >
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={handlePreviousPeriod}
            className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <DatePicker
            selected={startDate}
            onChange={(date) => {
              if (!date) return;
              const normalized = new Date(date);
              normalized.setHours(0, 0, 0, 0);
              setStartDate(normalized);
            }}
            maxDate={endDate}
            dateFormat="dd/MM/yyyy"
            locale={tr}
            className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center min-w-[140px]"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
          <span className="text-gray-500">-</span>
          <DatePicker
            selected={endDate}
            onChange={(date) => {
              if (!date) return;
              const normalized = new Date(date);
              normalized.setHours(23, 59, 59, 999);
              setEndDate(normalized);
            }}
            minDate={startDate}
            dateFormat="dd/MM/yyyy"
            locale={tr}
            className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center min-w-[140px]"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
          <button
            onClick={handleNextPeriod}
            className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 mr-2">Zaman Aralığı:</span>
          <button
            onClick={() => handlePeriodTypeChange('daily')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${periodType === 'daily' ? 'bg-primary-600 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
          >
            Günlük
          </button>
          <button
            onClick={() => handlePeriodTypeChange('monthly')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${periodType === 'monthly' ? 'bg-primary-600 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
          >
            Aylık
          </button>
          <button
            onClick={() => handlePeriodTypeChange('yearly')}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${periodType === 'yearly' ? 'bg-primary-600 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
          >
            Yıllık
          </button>
        </div>
      </PageToolbar>

      <div className="flex-1 overflow-hidden px-6 py-4">
        <div className="grid h-full gap-4 lg:grid-cols-[1.15fr_1fr]">
          <section className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
            <header className="border-b border-gray-200 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-black text-gray-900 mb-2 tracking-tight">ÇALIŞANLAR</h2>
              <p className="text-sm text-gray-600 font-medium">Çalışan prim bilgileri ve yönetimi</p>
            </header>
            <div className="flex-1 overflow-y-auto p-6">
              {employees.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <Users className="h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-lg font-medium text-gray-700 mb-1">Henüz çalışan eklenmemiş</p>
                  <p className="text-sm text-gray-500">Yeni bir çalışan ekleyerek başlayın</p>
                </div>
              ) : (
                <>
                  {/* Aylık Prim Özet Kartı */}
                  <div className="mb-4 bg-white p-4 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">Bu Ay Toplam Prim</h3>
                        <p className="text-xs text-gray-600 font-medium">{new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}</p>
                      </div>
                      <div className="text-2xl font-black text-green-600">
                        {Object.values(monthlyBonusesByEmployee).reduce((sum, bonus) => sum + bonus, 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-3">
                    {employees.map((employee) => {
                      const employeeBonus = bonusesByEmployee[employee.id] || 0;
                      const monthlyEmployeeBonus = monthlyBonusesByEmployee[employee.id] || 0;
                      return (
                        <li
                          key={employee.id}
                          className="bg-white p-4 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-200"
                        >
                          <div className="mb-3">
                            <h3 className="text-sm font-bold text-gray-900 mb-0.5">
                              {employee.first_name} {employee.last_name}
                            </h3>
                            <p className="text-xs text-gray-600">{employee.phone || 'Telefon bilgisi yok'}</p>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="group relative overflow-hidden">
                              <div className="absolute inset-0 bg-primary-50 rounded-lg"></div>
                              <div className="relative p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <p className="text-xs font-bold text-gray-700 mb-1 tracking-wide">SEÇİLİ DÖNEM</p>
                                <p className={`text-lg font-black ${employeeBonus > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                                  {employeeBonus > 0 ? employeeBonus.toLocaleString('tr-TR', { minimumFractionDigits: 0 }) : '0'} ₺
                                </p>
                              </div>
                            </div>

                            <div className="group relative overflow-hidden">
                              <div className="absolute inset-0 bg-green-50 rounded-lg"></div>
                              <div className="relative p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                                <p className="text-xs font-bold text-gray-700 mb-1 tracking-wide">BU AY</p>
                                <p className={`text-lg font-black ${monthlyEmployeeBonus > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                                  {monthlyEmployeeBonus.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-end gap-2 mt-3">
                            <button
                              onClick={() => handleEditEmployee(employee)}
                              disabled={isDeletingEmployee === employee.id}
                              className="rounded-md px-2.5 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Düzenle
                            </button>
                            <button
                              onClick={() => handleDeleteEmployee(employee.id)}
                              disabled={isDeletingEmployee === employee.id}
                              className="rounded-md px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isDeletingEmployee === employee.id ? 'Siliniyor...' : 'Sil'}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          </section>

          <section className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
            <header className="border-b border-gray-200 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-black text-gray-900 mb-2 tracking-tight">PRIMLER</h2>
              <p className="text-sm text-gray-600 font-medium">Seçili dönem prim kayıtları</p>
            </header>
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex h-full flex-col items-center justify-center">
                  <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-3"></div>
                  <p className="text-sm text-gray-500">Veriler yükleniyor...</p>
                </div>
              ) : bonuses.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <Award className="h-16 w-16 text-gray-300 mb-4" />
                  <p className="text-lg font-medium text-gray-700 mb-1">Prim bulunamadı</p>
                  <p className="text-sm text-gray-500">Seçili dönemde prim kaydı bulunmamaktadır</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {bonuses.map((bonus) => (
                    <li
                      key={bonus.id}
                      className="bg-white p-4 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-200"
                    >
                      <div className="mb-3">
                        <h3 className="text-sm font-bold text-gray-900 mb-0.5">
                          {bonus.employees?.first_name} {bonus.employees?.last_name}
                        </h3>
                        <p className="text-xs text-gray-600">
                          {formatDateForDisplay(bonus.bonus_date)}
                        </p>
                      </div>
                      <div className="mb-3">
                        <div className="group relative overflow-hidden">
                          <div className="absolute inset-0 bg-green-50 rounded-lg"></div>
                          <div className="relative p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                            <p className="text-xs font-bold text-gray-700 mb-1 tracking-wide">PRIM TUTARI</p>
                            <p className="text-xl font-black text-green-600">
                              {parseFloat(bonus.amount).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditBonus(bonus)}
                          disabled={isDeletingBonus === bonus.id}
                          className="rounded-md px-2.5 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Düzenle
                        </button>
                        <button
                          onClick={() => handleDeleteBonus(bonus.id)}
                          disabled={isDeletingBonus === bonus.id}
                          className="rounded-md px-2.5 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isDeletingBonus === bonus.id ? 'Siliniyor...' : 'Sil'}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Employee Modal */}
      {
        showEmployeeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800">
                  {editingEmployee ? 'Çalışan Düzenle' : 'Yeni Çalışan'}
                </h2>
                <button
                  onClick={() => {
                    setShowEmployeeModal(false);
                    resetEmployeeForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleEmployeeSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ad *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={employeeFormData.first_name}
                      onChange={handleEmployeeFormChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Soyad *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={employeeFormData.last_name}
                      onChange={handleEmployeeFormChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={employeeFormData.phone}
                      onChange={handleEmployeeFormChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={employeeFormData.email}
                      onChange={handleEmployeeFormChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      İşe Giriş Tarihi
                    </label>
                    <input
                      type="date"
                      name="hire_date"
                      value={employeeFormData.hire_date}
                      onChange={handleEmployeeFormChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmployeeModal(false);
                      resetEmployeeForm();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingEmployee}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingEmployee ? 'İşleniyor...' : (editingEmployee ? 'Güncelle' : 'Ekle')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Bonus Modal */}
      {
        showBonusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800">
                  {editingBonus ? 'Prim Düzenle' : 'Yeni Prim'}
                </h2>
                <button
                  onClick={() => {
                    setShowBonusModal(false);
                    resetBonusForm();
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleBonusSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Çalışan *
                    </label>
                    <select
                      name="employee_id"
                      value={bonusFormData.employee_id}
                      onChange={handleBonusFormChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">Seçiniz</option>
                      {employees.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.first_name} {employee.last_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tutar (₺) *
                    </label>
                    <input
                      type="number"
                      name="amount"
                      value={bonusFormData.amount}
                      onChange={handleBonusFormChange}
                      step="0.01"
                      min="0"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tarih *
                    </label>
                    <DatePicker
                      selected={bonusFormData.bonus_date ? new Date(bonusFormData.bonus_date + 'T00:00:00') : null}
                      onChange={(date) => {
                        if (!date) return;
                        const year = date.getFullYear();
                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                        const day = date.getDate().toString().padStart(2, '0');
                        const formattedDate = `${year}-${month}-${day}`;
                        setBonusFormData(prev => ({
                          ...prev,
                          bonus_date: formattedDate
                        }));
                      }}
                      dateFormat="dd/MM/yyyy"
                      locale={tr}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBonusModal(false);
                      resetBonusForm();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingBonus}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingBonus ? 'İşleniyor...' : (editingBonus ? 'Güncelle' : 'Ekle')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default Bonuses;
